import styled from 'styled-components';
import { ShoppingCart, Loader2, Clock, Zap } from 'lucide-react';
import SaudiRiyal from '../icons/SaudiRiyal';
import { Product } from '../../store/useProductStore';
import * as S from './styles';
import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { normalizeMediaUrl, onCdnImageError } from '../../lib/mediaUrl';

function formatPrepTime(min: number): string {
  if (min <= 0) return '';
  if (min === 60) return 'ساعة';
  if (min === 120) return 'ساعتين';
  if (min === 90) return 'ساعة ونصف';
  if (min % 60 === 0 && min > 0) return `${min / 60} ساعات`;
  if (min >= 60) return `${Math.floor(min / 60)} ساعة و${min % 60} دقيقة`;
  return `${min} دقيقة`;
}

interface ProductCardProps {
  product: Product;
  onAdd: (product: Product) => Promise<void> | void;
  onClick?: (product: Product) => void;
}

const ProductCard = ({ product, onAdd, onClick }: ProductCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [timeLeft, setTimeLeft] = useState(14 * 60);
  const descRef = useRef<HTMLParagraphElement>(null);
  const hiddenRef = useRef<HTMLParagraphElement>(null);
  const isSoldOut = product.soldOut === true;
  const isNew = product.new === true;
  const isPreOrder = product.preOrder === true;

  useLayoutEffect(() => {
    const checkIfTruncated = () => {
      if (!product.description) {
        setShowReadMore(false);
        return;
      }

      if (isExpanded) {
        setShowReadMore(true);
        return;
      }

      if (descRef.current && hiddenRef.current) {
        const visibleElement = descRef.current;
        const hiddenElement = hiddenRef.current;
        
        // ضبط نفس العرض والـ padding للعنصر المخفي
        const computedStyle = window.getComputedStyle(visibleElement);
        hiddenElement.style.width = `${visibleElement.offsetWidth}px`;
        hiddenElement.style.paddingLeft = computedStyle.paddingLeft;
        hiddenElement.style.paddingRight = computedStyle.paddingRight;
        
        // قياس الارتفاع الفعلي للنص الكامل
        const fullHeight = hiddenElement.scrollHeight;
        // قياس الارتفاع المرئي للعنصر المقطوع
        const visibleHeight = visibleElement.clientHeight;
        
        // التحقق من أن النص مقطوع فعليًا (مع هامش صغير للأخطاء)
        const isTextTruncated = Math.abs(fullHeight - visibleHeight) > 2;
        setShowReadMore(isTextTruncated);
      }
    };

    // استخدام requestAnimationFrame لضمان أن DOM قد تم تحديثه
    const rafId = requestAnimationFrame(() => {
      checkIfTruncated();
    });
    
    const resizeObserver = new ResizeObserver(() => {
      checkIfTruncated();
    });

    if (descRef.current) {
      resizeObserver.observe(descRef.current);
    }
    
    window.addEventListener('resize', checkIfTruncated);
    
    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkIfTruncated);
    };
  }, [product.description, isExpanded]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          return 14 * 60;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(product);
    }
  };

  return (
    <S.ProductCard $soldOut={isSoldOut} onClick={handleCardClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <S.ProductImage>
        <S.ImageClip>
          <S.Img
            src={normalizeMediaUrl(product.image)}
            alt={product.title}
            onError={onCdnImageError}
          />
        </S.ImageClip>
        {/* {product.originalPrice && (
          <S.TimerBadge>{formatCountdown(timeLeft)}<Zap size={8} fill="currentColor" /></S.TimerBadge>
        )} */}
      </S.ProductImage>
      <S.TitleWrapper>
        <S.ProductTitle>{product.title}</S.ProductTitle>
        {isNew && <S.NewBadge>جديدنا</S.NewBadge>}
        {isPreOrder && <S.PreOrderBadge>حجز مسبق</S.PreOrderBadge>}
      </S.TitleWrapper>
      <S.ProductDetails>
        <S.DescriptionWrapper>
          <S.ProductDesc ref={descRef} $isExpanded={isExpanded}>{product.description}</S.ProductDesc>
          <S.HiddenDesc ref={hiddenRef}>{product.description}</S.HiddenDesc>
          {showReadMore && (
            <S.ReadMoreButton onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? 'أقل' : 'المزيد'}
            </S.ReadMoreButton>
          )}
          {product.prepTimeMinutes != null && product.prepTimeMinutes > 0 && (
            <S.PrepTimeText>
              <Clock size={12} /> مدة التحضير: {formatPrepTime(product.prepTimeMinutes)}
            </S.PrepTimeText>
          )}
        </S.DescriptionWrapper>
        <S.ProductPrice>
          <S.CurrentPrice>
            {product.price.toFixed(2)} <SaudiRiyal size={14} style={{ marginRight: '4px' }} />
          </S.CurrentPrice>
          {product.originalPrice && (
            <S.OldPrice>
              {product.originalPrice.toFixed(2)} <SaudiRiyal size={12} style={{ marginRight: '2px' }} />
            </S.OldPrice>
          )}
        </S.ProductPrice>
      </S.ProductDetails>
      <S.ProductAction>
        {isSoldOut ? (
          <S.SoldOutBadge>نفذ</S.SoldOutBadge>
        ) : (
          <S.AddButton 
            disabled={isAdding} 
            onClick={async (e) => {
              e.stopPropagation(); // منع فتح الـ popup عند النقر على زر الإضافة
              if (isAdding) return;
              setIsAdding(true);
              try {
                await onAdd(product);
              } finally {
                setIsAdding(false);
              }
            }}
          >
            {isAdding ? (
              <Loader2 size={14} className="spinner" />
            ) : (
              <>
                إضافة
                <ShoppingCart size={12} strokeWidth={2.25} />
              </>
            )}
          </S.AddButton>
        )}
      </S.ProductAction>
    </S.ProductCard>
  );
};

export default ProductCard;

